import Commons from 'oc-commons/Commons';

/**
 * Stellt ein Undo / Redo Feature für Configurations bereit. Configurations werden automatisch
 * gespeichert, wenn das changedConfiguration - Event auf der APP ausgelöst wird.
 *
 * Die APP registriert standardmäßig einen Undohandler als APP.undoHandler.
 *
 * ACHTUNG: Wird erst aktiv durch APP.settings.undo.enabled=true
 *
 * Falls die Undo-History sich geändert hat, wird ein **undoHistoryChanged** Event auf der APP
 * ausgelöst.
 *
 * @example
 * $('#undoButton').onSafe('tap',()=>{
 *    APP.undoHandler.undo();
 * });
 * $('#redoButton').onSafe('tap',()=>{
 *    APP.undoHandler.redo();
 * });
 * APP.on('undoHistoryChanged',()=>{
 *    $('#undoButton').disable(!APP.undoHandler.canUndo());
 *    $('#redoButton').disable(!APP.undoHandler.canRedo());
 * });
 */
export default class UndoHandler {

	/** */
	constructor() {
		this.insideOwnSetConfiguration = false;
		this.stack = [];
		this.pointer = -1;
		//this.debounceTimer = 2000;

		// speichert automatisch die Configuration bei Änderungen
		// ein höherer Wert hier (2s) ist sinnvoll, um rückkoppelnde Änderungen
		// abzuwarten (diese treten z.B. im Zaunplaner auf)
		const saveDebounced = _.debounce(()=>this.save(),this.debounceTimer);
        this.saveCounter = 0;
		APP.on('changedConfiguration',()=>{
			if (this.saveCounter==0) {
				this.saveCounter++;
				try {
					if (!this.insideOwnSetConfiguration) {
						saveDebounced();
					}
				}finally {
					this.saveCounter--;
				}
			}
		});
	}

	/**
	 * wird über das changedConfiguration-Event aufgerufen
	 */
	save() {
		if (!_.get(APP.settings,'undo.enabled') || this.insideOwnSetConfiguration) {
			return;
		}

		const newConfig = this.getStageAsConfigurationForUndo();
        if (newConfig==null) {
            return;
        }
		const lastConfig = this.stack[this.pointer];

		// der Vergleich, zusammen mit cloneDeep ist wichtig, damit auch bei debounced changedConfigurations
		// verhindert wird, dass der Undohandler durch sich selbst ausgelöste Änderungen ignoriert
		if (this.isEqualConfiguration(lastConfig,newConfig)) {
			return;
		}

		// Zu Debugzwecken kann es hilfreich sein, sich die Unterschiede vorher/nachher anzuzeigen
		// console.log('Undo-Entry because ',Commons.deepDiff(lastConfig,newConfig));

		// Remove all cfgs after the current position
		this.stack.splice(this.pointer+1, this.stack.length);

		if (this.stack.length>=APP.settings.undo.max) {
			this.stack.shift();
		}

		this.stack.push(Commons.cloneDeep(newConfig));
		this.pointer = this.stack.length-1;

		APP.trigger('undoHistoryChanged');
	}

    getStageAsConfigurationForUndo() {
        return APP.getStageAsConfiguration();
    }

    isEqualConfiguration(lastConfig,newConfig) {
        return Commons.isEqualEpsilon(lastConfig,newConfig);
    }

	/** */
	canUndo() {
		return this.pointer > 0;
	}

	/** */
	undo() {
		if (!this.canUndo()) {
			return;
		}
		this.pointer--;
		this._setConfigurationFromPointer();
	}

	/** */
	canRedo() {
		return this.pointer < this.stack.length-1;
	}

	/** */
	redo() {
		if (!this.canRedo()) {
			return;
		}
		this.pointer++;
		this._setConfigurationFromPointer();
	}

	/**
	 * @private
     */
	_setConfigurationFromPointer() {
		try {
			this.insideOwnSetConfiguration = true;
			APP.setStageFromConfiguration(Commons.cloneDeep(this.stack[this.pointer]));
			STAGE.dirty();
            APP.trigger('undoHistoryChanged');
        } finally {
			this.insideOwnSetConfiguration = false;
		}
	}


}
